import { FormEvent, PointerEvent as ReactPointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiError,
  PagedResult,
  PlaybackSession,
  PlayableVideoSource,
  RecordingQueryHandle,
  RecordingQueryResult,
  RecordingSegment,
  api,
} from '../lib/api';
import { OapmPlayer, OapmPlayerHandle, PlayerStatus } from '../components/OapmPlayer';
import { useI18n } from '../i18n';
import { DatePicker } from '../components/DatePicker';

const daySeconds = 86_400;
const playbackRates = [0.25, 0.5, 1, 1.5, 2, 3, 4] as const;
const queryDeadlineMs = 30_000;

type TimelineSegment = {
  key: string;
  segment: RecordingSegment;
  startUnix: number;
  endUnix: number;
  clippedStartUnix: number;
  clippedEndUnix: number;
  startSecond: number;
  endSecond: number;
  left: number;
  width: number;
};


function onlineLabel(status: string): string {
  if (status === 'ONLINE') return '在线';
  if (status === 'OFFLINE') return '离线';
  return '未知';
}

function segmentKey(segment: RecordingSegment): string {
  return `${segment.ssrcId}-${segment.startUnixTimeSeconds}`;
}

function formatDayTime(value: number): string {
  const normalized = Math.min(daySeconds - 1, Math.max(0, Math.floor(value)));
  const hours = Math.floor(normalized / 3600);
  const minutes = Math.floor((normalized % 3600) / 60);
  const seconds = normalized % 60;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
}

function localDateValue(value: Date): string {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function formatNvrDateTime(value: string): string {
  const normalized = value.replace('T', ' ').trim();
  const datePart = normalized.slice(0, 10);
  const timePart = normalized.slice(11, 19);
  if (!timePart) return normalized;
  return `${datePart.slice(5)} ${timePart}`;
}

function isAbortError(reason: unknown): boolean {
  return reason instanceof DOMException && reason.name === 'AbortError';
}

function sleep(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, milliseconds);
    const abort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', abort, { once: true });
  });
}

async function apiWithTimeout<T>(
  path: string,
  init: RequestInit,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<T> {
  const controller = new AbortController();
  const externalSignal = init.signal;
  const abortFromExternal = () => controller.abort();
  if (externalSignal?.aborted) controller.abort();
  else externalSignal?.addEventListener('abort', abortFromExternal, { once: true });

  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await api<T>(path, { ...init, signal: controller.signal });
  } catch (reason) {
    if (controller.signal.aborted && !externalSignal?.aborted) {
      throw new ApiError(504, 'CLIENT_TIMEOUT', timeoutMessage);
    }
    throw reason;
  } finally {
    window.clearTimeout(timer);
    externalSignal?.removeEventListener('abort', abortFromExternal);
  }
}

export function PlaybackPage() {
  const { t, language } = useI18n();
  const today = localDateValue(new Date());
  const [sources, setSources] = useState<PlayableVideoSource[]>([]);
  const [sourceSearch, setSourceSearch] = useState('');
  const [videoSourceId, setVideoSourceId] = useState<number>();
  const [date, setDate] = useState(today);
  const [segments, setSegments] = useState<RecordingSegment[]>([]);
  const [queryResult, setQueryResult] = useState<RecordingQueryResult>();
  const [queryTaskId, setQueryTaskId] = useState<number>();
  const [session, setSession] = useState<PlaybackSession>();
  const [activeSegmentKey, setActiveSegmentKey] = useState<string>();
  const [status, setStatus] = useState(() => t('playback.selectSource'));
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [loadingSources, setLoadingSources] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [openingPlayback, setOpeningPlayback] = useState(false);
  const [playheadSeconds, setPlayheadSeconds] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(0.65);
  const [muted, setMuted] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [audioUnavailable, setAudioUnavailable] = useState(false);
  const [playerStatus, setPlayerStatus] = useState<PlayerStatus>('idle');
  const [playerPaused, setPlayerPaused] = useState(false);
  const [seeking, setSeeking] = useState(false);
  const [timelinePreviewSeconds, setTimelinePreviewSeconds] = useState<number>();
  const [timelineDragging, setTimelineDragging] = useState(false);

  const sessionRef = useRef<PlaybackSession | undefined>(undefined);
  const activeSegmentKeyRef = useRef<string | undefined>(undefined);
  const playheadRef = useRef(0);
  const dayStartUnixRef = useRef(0);
  const playbackStageRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<OapmPlayerHandle | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const queryAbortRef = useRef<AbortController | undefined>(undefined);
  const queryGenerationRef = useRef(0);
  const queryingRef = useRef(false);
  const playbackGenerationRef = useRef(0);
  const rebuildInFlightRef = useRef(false);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    activeSegmentKeyRef.current = activeSegmentKey;
  }, [activeSegmentKey]);

  useEffect(() => {
    dayStartUnixRef.current = queryResult?.dayStartUnixTimeSeconds ?? 0;
  }, [queryResult?.dayStartUnixTimeSeconds]);

  function updatePlayhead(value: number) {
    const next = Math.min(daySeconds - 1, Math.max(0, value));
    playheadRef.current = next;
    setPlayheadSeconds(next);
  }

  function timelineSecondFromPointer(event: ReactPointerEvent<HTMLElement>): number {
    const timeline = timelineRef.current;
    if (!timeline) return playheadRef.current;
    const rect = timeline.getBoundingClientRect();
    if (rect.width <= 0) return playheadRef.current;
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    return Math.min(daySeconds - 1, Math.max(0, Math.round(ratio * daySeconds)));
  }

  function previewTimeline(event: ReactPointerEvent<HTMLElement>) {
    const second = timelineSecondFromPointer(event);
    setTimelinePreviewSeconds(second);
    if (timelineDragging) updatePlayhead(second);
  }

  function beginTimelineDrag(event: ReactPointerEvent<HTMLInputElement>) {
    const second = timelineSecondFromPointer(event);
    setTimelineDragging(true);
    setTimelinePreviewSeconds(second);
    updatePlayhead(second);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function finishTimelineDrag(event: ReactPointerEvent<HTMLInputElement>) {
    const second = timelineSecondFromPointer(event);
    setTimelineDragging(false);
    setTimelinePreviewSeconds(second);
    updatePlayhead(second);
    void seekToDaySecond(second);
  }

  useEffect(() => {
    void api<PagedResult<PlayableVideoSource>>('/video-sources?supportsRecording=true&page=1&pageSize=200')
      .then((result) => {
        setSources(result.items);
        if (result.items.length > 0) {
          setVideoSourceId(result.items[0]?.id);
          setStatus(t('playback.waitQuery'));
        } else {
          setStatus(t('playback.noSources'));
        }
      })
      .catch((reason) => setError(reason instanceof ApiError ? reason.message : t('playback.sourcesLoadFailed')))
      .finally(() => setLoadingSources(false));
  }, []);

  useEffect(() => {
    if (!session) return;
    const timer = window.setInterval(() => {
      void api<void>(
        `/playback/sessions/${session.sessionId}/heartbeat`,
        { method: 'POST' },
        {
          timeoutMs: 5_000,
          timeoutMessage: t('playback.heartbeatTimeout'),
        },
      );
    }, 20_000);
    return () => window.clearInterval(timer);
  }, [session]);

  useEffect(() => {
    const closeCurrent = () => {
      queryAbortRef.current?.abort();
      const current = sessionRef.current;
      if (current) {
        void fetch(`/api/playback/sessions/${current.sessionId}`, {
          method: 'DELETE',
          credentials: 'include',
          keepalive: true,
        });
      }
    };
    window.addEventListener('beforeunload', closeCurrent);
    return () => {
      window.removeEventListener('beforeunload', closeCurrent);
      closeCurrent();
    };
  }, []);

  const selectedSource = sources.find((source) => source.id === videoSourceId);
  const filteredSources = useMemo(() => {
    const needle = sourceSearch.trim().toLocaleLowerCase();
    return sources.filter((source) => !needle || [source.displayName, source.areaName, source.nvrDisplayName]
      .some((value) => value?.toLocaleLowerCase().includes(needle)));
  }, [sourceSearch, sources]);

  const sourcesByArea = useMemo(() => {
    const groups = new Map<string, PlayableVideoSource[]>();
    for (const source of filteredSources) {
      const key = source.areaName ?? t('playback.unclassified');
      groups.set(key, [...(groups.get(key) ?? []), source]);
    }
    return Array.from(groups.entries());
  }, [filteredSources, t]);

  const timelineSegments = useMemo<TimelineSegment[]>(() => {
    const dayStartUnix = queryResult?.dayStartUnixTimeSeconds;
    if (dayStartUnix === undefined) return [];
    const dayEndUnix = dayStartUnix + daySeconds;

    return segments.flatMap((segment) => {
      const startUnix = Number(segment.startUnixTimeSeconds);
      let endUnix = Number(segment.endUnixTimeSeconds);
      if (!Number.isFinite(startUnix) || !Number.isFinite(endUnix)) return [];
      if (endUnix <= startUnix) endUnix += daySeconds;
      const clippedStartUnix = Math.max(startUnix, dayStartUnix);
      const clippedEndUnix = Math.min(endUnix, dayEndUnix);
      if (clippedEndUnix <= clippedStartUnix) return [];
      const startSecond = clippedStartUnix - dayStartUnix;
      const endSecond = clippedEndUnix - dayStartUnix;
      return [{
        key: segmentKey(segment),
        segment,
        startUnix,
        endUnix,
        clippedStartUnix,
        clippedEndUnix,
        startSecond,
        endSecond,
        left: (startSecond / daySeconds) * 100,
        width: Math.max(0.2, ((endSecond - startSecond) / daySeconds) * 100),
      }];
    }).sort((left, right) => left.clippedStartUnix - right.clippedStartUnix);
  }, [queryResult?.dayStartUnixTimeSeconds, segments]);

  const activeTimelineSegment = timelineSegments.find((item) => item.key === activeSegmentKey);

  useEffect(() => {
    if (!session || playerStatus !== 'playing' || playerPaused) return;
    let previous = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      const elapsed = ((now - previous) / 1000) * playbackRate;
      previous = now;
      const upperBound = Math.min(daySeconds - 1, activeTimelineSegment?.endSecond ?? daySeconds - 1);
      updatePlayhead(Math.min(upperBound, playheadRef.current + elapsed));
    }, 250);
    return () => window.clearInterval(timer);
  }, [activeTimelineSegment?.endSecond, playbackRate, playerPaused, playerStatus, session]);

  useEffect(() => {
    if (!session && !querying && !openingPlayback && !seeking && segments.length === 0) {
      setStatus(videoSourceId ? t('playback.waitQuery') : t('playback.selectSource'));
    }
  }, [language, openingPlayback, querying, seeking, segments.length, session, t, videoSourceId]);

  useEffect(() => {
    if (playerPaused && session && !seeking) {
      setStatus(t('playback.pausedAt', { time: formatDayTime(playheadRef.current) }));
      return;
    }
    if (playerStatus === 'playing' && session && !seeking) {
      setStatus(t('playback.playingAt', { time: formatDayTime(playheadRef.current) }));
    }
  }, [playerPaused, playerStatus, seeking, session?.sessionId]);

  async function rebuildCurrentPlayback(
    segment: RecordingSegment,
    targetUnixTimeSeconds: number,
  ) {
    const current = sessionRef.current;
    if (!current || rebuildInFlightRef.current || !queryResult) return;

    const generation = playbackGenerationRef.current;
    const key = segmentKey(segment);
    const offsetSeconds = Math.max(0, Math.floor(targetUnixTimeSeconds - segment.startUnixTimeSeconds));
    rebuildInFlightRef.current = true;
    setSeeking(true);
    setError(undefined);
    setNotice(undefined);
    setAudioEnabled(false);
    setAudioUnavailable(false);
    setMuted(true);
    setPlayerPaused(false);
    setStatus(t('playback.rebuildAt', { time: formatDayTime(targetUnixTimeSeconds - queryResult.dayStartUnixTimeSeconds) }));

    try {
      const rebuilt = await apiWithTimeout<PlaybackSession>(
        `/playback/sessions/${current.sessionId}/seek`,
        {
          method: 'POST',
          body: JSON.stringify({ offsetSeconds }),
        },
        35_000,
        t('playback.rebuildTimeout'),
      );

      const stillCurrent = generation === playbackGenerationRef.current &&
        sessionRef.current?.sessionId === current.sessionId &&
        activeSegmentKeyRef.current === key;
      if (!stillCurrent) return;

      sessionRef.current = rebuilt;
      setSession(rebuilt);
      setPlayerStatus('connecting');
      updatePlayhead(targetUnixTimeSeconds - queryResult.dayStartUnixTimeSeconds);
      setStatus(t('playback.targetReady'));
      setNotice(t('playback.reinviteNotice'));
    } catch (reason) {
      if (!isAbortError(reason) && sessionRef.current?.sessionId === current.sessionId) {
        const message = reason instanceof ApiError
          ? reason.message
          : reason instanceof Error ? reason.message : t('playback.rebuildFailed');
        setError(message);
        setStatus(t('playback.originalContinues'));
      }
    } finally {
      rebuildInFlightRef.current = false;
      setSeeking(false);
    }
  }

  async function closePlayback(invalidateGeneration = true) {
    if (invalidateGeneration) playbackGenerationRef.current += 1;
    setSeeking(false);

    const current = sessionRef.current;
    if (current && sessionRef.current?.sessionId === current.sessionId) {
      sessionRef.current = undefined;
      activeSegmentKeyRef.current = undefined;
      setSession(undefined);
      setActiveSegmentKey(undefined);
      setPlayerStatus('idle');
      setPlayerPaused(false);
    } else if (!current) {
      setSession(undefined);
      setActiveSegmentKey(undefined);
      setPlayerStatus('idle');
      setPlayerPaused(false);
      return;
    }

    void api<void>(
      `/playback/sessions/${current.sessionId}`,
      { method: 'DELETE' },
      {
        timeoutMs: 6_000,
        timeoutMessage: t('playback.stopUnconfirmedLocal'),
      },
    ).catch((reason) => {
      if (!isAbortError(reason)) {
        setNotice(
          reason instanceof ApiError
            ? reason.message
            : t('playback.stopBackgroundCleanup'),
        );
      }
    });
  }

  function cancelActiveQuery() {
    queryGenerationRef.current += 1;
    queryAbortRef.current?.abort();
    queryAbortRef.current = undefined;
    queryingRef.current = false;
    setQuerying(false);
  }

  async function resetQueryState(nextStatus = t('playback.waitQuery')) {
    cancelActiveQuery();
    await closePlayback();
    setSegments([]);
    setQueryResult(undefined);
    setQueryTaskId(undefined);
    setError(undefined);
    setNotice(undefined);
    setAudioEnabled(false);
    setAudioUnavailable(false);
    setMuted(true);
    updatePlayhead(0);
    setStatus(nextStatus);
  }

  async function changeSource(value: string) {
    const nextId = Number(value) || undefined;
    await resetQueryState(nextId ? t('playback.waitQuery') : t('playback.selectSource'));
    setVideoSourceId(nextId);
  }

  async function changeDate(value: string) {
    await resetQueryState(t('playback.queryDateChanged'));
    setDate(value);
  }

  async function query(event: FormEvent) {
    event.preventDefault();
    if (!videoSourceId) {
      setError(t('playback.chooseSourceError'));
      return;
    }
    if (queryingRef.current) return;

    await resetQueryState(t('playback.creatingQuery'));
    const generation = ++queryGenerationRef.current;
    const controller = new AbortController();
    queryAbortRef.current = controller;
    queryingRef.current = true;
    setQuerying(true);
    setError(undefined);
    setNotice(undefined);

    try {
      const handle = await apiWithTimeout<RecordingQueryHandle>(
        '/playback/queries',
        {
          method: 'POST',
          body: JSON.stringify({ videoSourceId, date }),
          signal: controller.signal,
        },
        18_000,
        t('playback.queryTimeout'),
      );
      if (generation !== queryGenerationRef.current) return;

      setQueryTaskId(handle.taskId);
      setStatus(t('playback.queryTask', { taskId: handle.taskId }));
      const deadline = Date.now() + queryDeadlineMs;
      let result: RecordingQueryResult | undefined;

      while (Date.now() < deadline && generation === queryGenerationRef.current) {
        try {
          result = await apiWithTimeout<RecordingQueryResult>(
            `/playback/queries/${handle.taskId}?videoSourceId=${videoSourceId}&date=${encodeURIComponent(date)}`,
            { signal: controller.signal },
            6_000,
            t('playback.readResultTimeout'),
          );
          setStatus(result.message ?? t('playback.receivingCatalog', { received: result.receivedCount, total: Math.max(result.totalCount, result.receivedCount) }));
          if (result.completed) break;
        } catch (reason) {
          if (isAbortError(reason)) throw reason;
          if (!(reason instanceof ApiError) || reason.status !== 404) throw reason;
        }
        await sleep(600, controller.signal);
      }

      if (generation !== queryGenerationRef.current) return;
      if (!result) {
        throw new ApiError(504, 'RECORDING_QUERY_TIMEOUT', t('playback.queryTimeout'));
      }

      const found = [...result.segments]
        .sort((left, right) => left.startUnixTimeSeconds - right.startUnixTimeSeconds);
      setQueryResult(result);
      setSegments(found);
      if (found.length > 0) {
        updatePlayhead(Math.max(0, found[0]!.startUnixTimeSeconds - result.dayStartUnixTimeSeconds));
      }

      if (!result.completed) {
        setNotice(t('playback.queryPartial', { seconds: Math.round(queryDeadlineMs / 1000), count: found.length }));
      }
      setStatus(found.length > 0
        ? t('playback.foundSegments', { count: found.length, suffix: result.completed ? '' : t('playback.incompleteSuffix') })
        : t('playback.noRecordingDay'));
    } catch (reason) {
      if (!isAbortError(reason) && generation === queryGenerationRef.current) {
        setError(reason instanceof ApiError ? reason.message : t('playback.queryFailed'));
        setStatus(t('playback.queryFailedStatus'));
      }
    } finally {
      if (generation === queryGenerationRef.current) {
        queryAbortRef.current = undefined;
        queryingRef.current = false;
        setQuerying(false);
      }
    }
  }

  async function playAt(segment: RecordingSegment, targetUnixTimeSeconds?: number) {
    if (!videoSourceId || !queryTaskId || !queryResult || openingPlayback || seeking) return;
    const key = segmentKey(segment);
    const target = Math.min(
      Math.max(segment.startUnixTimeSeconds, targetUnixTimeSeconds ?? segment.startUnixTimeSeconds),
      Math.max(segment.startUnixTimeSeconds, segment.endUnixTimeSeconds - 1),
    );
    setError(undefined);
    setNotice(undefined);

    const current = sessionRef.current;
    if (current && activeSegmentKeyRef.current === key) {
      await rebuildCurrentPlayback(segment, target);
      return;
    }

    setOpeningPlayback(true);
    await closePlayback();
    const generation = playbackGenerationRef.current;
    const offsetSeconds = Math.max(0, Math.floor(target - segment.startUnixTimeSeconds));
    setStatus(t('playback.openingAt', { time: formatDayTime(target - queryResult.dayStartUnixTimeSeconds) }));
    setAudioEnabled(false);
    setAudioUnavailable(false);
    setMuted(true);

    try {
      const active = await apiWithTimeout<PlaybackSession>(
        '/playback/sessions',
        {
          method: 'POST',
          body: JSON.stringify({
            videoSourceId,
            taskId: queryTaskId,
            ssrcId: segment.ssrcId,
            offsetSeconds,
          }),
        },
        35_000,
        t('playback.openTimeout'),
      );

      if (generation !== playbackGenerationRef.current) {
        void api<void>(
          `/playback/sessions/${active.sessionId}`,
          { method: 'DELETE' },
          {
            timeoutMs: 6_000,
            timeoutMessage: t('playback.releaseExpiredTimeout'),
          },
        );
        return;
      }

      sessionRef.current = active;
      activeSegmentKeyRef.current = key;
      setSession(active);
      setActiveSegmentKey(key);
      setPlayerStatus('connecting');
      updatePlayhead(target - queryResult.dayStartUnixTimeSeconds);
      setStatus(t('playback.startedWaiting'));
      if (offsetSeconds > 1) {
        setNotice(t('playback.reinviteDirect'));
      }
    } catch (reason) {
      if (!isAbortError(reason) && generation === playbackGenerationRef.current) {
        setError(reason instanceof ApiError ? reason.message : reason instanceof Error ? reason.message : t('playback.playFailed'));
        setStatus(t('playback.playFailedStatus'));
      }
    } finally {
      setOpeningPlayback(false);
    }
  }

  async function seekToDaySecond(value: number) {
    if (timelineSegments.length === 0 || !queryResult || openingPlayback) return;
    const targetUnix = queryResult.dayStartUnixTimeSeconds + Math.floor(value);
    const match = timelineSegments.find((item) => targetUnix >= item.startUnix && targetUnix < item.endUnix);
    if (!match) {
      setError(t('playback.noRecordingAt', { time: formatDayTime(value) }));
      setStatus(t('playback.noRecordingStatus'));
      return;
    }
    await playAt(match.segment, targetUnix);
  }

  function toggleAudio() {
    if (!session) {
      setNotice(t('playback.selectFirst'));
      return;
    }
    if (audioUnavailable) {
      setNotice(t('playback.audioUnsupportedRetry'));
      return;
    }
    if (!audioEnabled) {
      setAudioEnabled(true);
      setMuted(false);
      setStatus(t('playback.tryingAudio'));
      return;
    }
    setMuted((value) => !value);
  }

  function changeVolume(value: number) {
    setVolume(value);
    if (audioUnavailable) return;
    if (value > 0) {
      setAudioEnabled(true);
      setMuted(false);
    } else {
      setMuted(true);
    }
  }

  function handleAudioUnsupported() {
    setAudioEnabled(false);
    setMuted(true);
    setAudioUnavailable(true);
    setError(undefined);
    setNotice(t('playback.audioUnavailableNotice'));
    setStatus(t('playback.audioUnavailableStatus'));
  }

  function skipPlayback(deltaSeconds: number) {
    if (!session || !queryResult || seeking || openingPlayback) {
      if (!session) setNotice(t('playback.selectFirst'));
      return;
    }
    const target = Math.min(daySeconds - 1, Math.max(0, playheadRef.current + deltaSeconds));
    updatePlayhead(target);
    void seekToDaySecond(target);
  }

  function navigateSegment(direction: -1 | 1) {
    if (timelineSegments.length === 0 || openingPlayback || seeking) return;
    const activeIndex = timelineSegments.findIndex((item) => item.key === activeSegmentKeyRef.current);
    const baseIndex = activeIndex >= 0 ? activeIndex : 0;
    const targetIndex = Math.min(timelineSegments.length - 1, Math.max(0, baseIndex + direction));
    const target = timelineSegments[targetIndex];
    if (!target || targetIndex === activeIndex) return;
    void playAt(target.segment, target.startUnix);
  }

  function fullscreenPlayback() {
    if (playbackStageRef.current?.requestFullscreen) {
      void playbackStageRef.current.requestFullscreen();
    }
  }

  return (
    <section className="page-section playback-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">NVR PLAYBACK</p>
          <h2>{t('playback.title')}</h2>
        </div>
        <span className="status-pill">
          {querying ? t('playback.statusQuerying') : openingPlayback ? t('playback.statusOpening') : seeking ? t('playback.statusSeeking') : status}
        </span>
      </header>

      <form className="playback-source-form" onSubmit={query}>
        <input
          value={sourceSearch}
          onChange={(event) => setSourceSearch(event.target.value)}
          placeholder={t('playback.searchSource')}
        />
        <select
          value={videoSourceId ?? ''}
          onChange={(event) => void changeSource(event.target.value)}
          disabled={loadingSources || querying || openingPlayback || seeking}
          required
        >
          <option value="">{t('playback.chooseSource')}</option>
          {sourcesByArea.map(([areaName, items]) => (
            <optgroup key={areaName} label={areaName}>
              {items.map((source) => <option key={source.id} value={source.id}>{source.displayName}</option>)}
            </optgroup>
          ))}
        </select>
        <DatePicker
          value={date}
          onChange={(value) => void changeDate(value)}
          disabled={querying || openingPlayback || seeking}
          ariaLabel={t('playback.dateLabel')}
        />
        <button className="primary-button" type="submit" disabled={!videoSourceId || querying || openingPlayback || seeking}>
          {querying ? t('playback.querying') : t('playback.query')}
        </button>
        {session && <button type="button" onClick={() => void closePlayback()} disabled={openingPlayback || seeking}>{t('common.stop')}</button>}
      </form>

      {selectedSource && (
        <div className="playback-source-summary">
          <div>
            <strong>{selectedSource.displayName}</strong>
            <span>{selectedSource.areaName ?? t('playback.unclassified')} · {selectedSource.nvrDisplayName ?? t('playback.unknownNvr')}</span>
          </div>
          <span className={`catalog-status ${selectedSource.onlineStatus === 'ONLINE' ? 'online' : 'offline'}`}>
            {selectedSource.onlineStatus === 'ONLINE' ? t('common.online') : selectedSource.onlineStatus === 'OFFLINE' ? t('common.offline') : t('common.unknown')}
          </span>
        </div>
      )}

      {queryResult && (
        <div className="playback-query-meta">
          {t('playback.nvrClock', {
            date: queryResult.queryDate,
            zone: queryResult.recordingTimeZoneId,
            received: queryResult.receivedCount,
            total: queryResult.totalCount < 0 ? '?' : queryResult.totalCount,
          })}
        </div>
      )}
      {notice && <div className="notice">{notice}</div>}
      {error && <div className="notice error-notice">{error}</div>}

      <div className="playback-layout">
        <div className="playback-stage" ref={playbackStageRef}>
          <div className="playback-video-panel">
            <OapmPlayer
              ref={playerRef}
              url={session?.mediaUrl}
              isLive={false}
              label={selectedSource?.displayName}
              videoOnly={!audioEnabled}
              muted={muted || !audioEnabled}
              volume={volume}
              playbackRate={playbackRate}
              mediaShardKey={selectedSource?.id}
              onStatusChange={setPlayerStatus}
              onPlaybackPausedChange={setPlayerPaused}
              onAudioUnsupported={handleAudioUnsupported}
              onPrevious={() => navigateSegment(-1)}
              onNext={() => navigateSegment(1)}
              onSkipBackward={() => skipPlayback(-10)}
              onSkipForward={() => skipPlayback(10)}
              onStop={() => void closePlayback()}
              previousDisabled={timelineSegments.findIndex((item) => item.key === activeSegmentKey) <= 0}
              nextDisabled={timelineSegments.findIndex((item) => item.key === activeSegmentKey) < 0 || timelineSegments.findIndex((item) => item.key === activeSegmentKey) >= timelineSegments.length - 1}
              skipDisabled={!session || seeking || openingPlayback}
              stopDisabled={!session || seeking || openingPlayback}
            />
          </div>

          <div className="playback-controls playback-command-bar">
            <div className="playback-primary-controls">
              <button type="button" onClick={() => playerRef.current?.togglePlayback()} disabled={!session || playerStatus !== 'playing'}>
                {playerPaused ? t('common.play') : t('common.pause')}
              </button>
              <button type="button" onClick={() => skipPlayback(-10)} disabled={!session || seeking || openingPlayback}>{t('common.back10')}</button>
              <button type="button" onClick={() => skipPlayback(10)} disabled={!session || seeking || openingPlayback}>{t('common.forward10')}</button>
              <button type="button" className="playback-stop-button" onClick={() => void closePlayback()} disabled={!session || openingPlayback || seeking}>{t('common.stop')}</button>
            </div>
            <button type="button" onClick={toggleAudio} disabled={!session || audioUnavailable}>
              {audioUnavailable ? t('playback.audioUnsupported') : !audioEnabled || muted ? t('playback.enableAudio') : t('playback.mute')}
            </button>
            <label className="volume-control">
              <span>{t('playback.volume')}</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                onChange={(event) => changeVolume(Number(event.target.value))}
                disabled={!session || audioUnavailable}
              />
            </label>
            <span className="playback-current-time">{formatDayTime(playheadSeconds)}</span>
            <label className="rate-control">
              <span>{t('playback.rate')}</span>
              <select value={playbackRate} onChange={(event) => setPlaybackRate(Number(event.target.value))}>
                {playbackRates.map((rate) => <option key={rate} value={rate}>{rate}×</option>)}
              </select>
            </label>
            <button type="button" onClick={fullscreenPlayback}>{t('common.fullscreen')}</button>
          </div>

          <div
            className="timeline playback-global-timeline"
            ref={timelineRef}
            onPointerMove={previewTimeline}
            onPointerLeave={() => { if (!timelineDragging) setTimelinePreviewSeconds(undefined); }}
          >
            <div className="timeline-track-rail" aria-hidden="true" />
            <div className="timeline-segment-layer">
              {timelineSegments.map((item) => (
                <span
                  key={`bar-${item.key}`}
                  className={`timeline-segment ${activeSegmentKey === item.key ? 'active' : ''}`}
                  style={{ left: `${item.left}%`, width: `${item.width}%` }}
                  title={`${item.segment.startLocalDateTime} → ${item.segment.endLocalDateTime}`}
                />
              ))}
            </div>
            <div className="timeline-playhead" style={{ left: `${(playheadSeconds / daySeconds) * 100}%` }} />
            {timelinePreviewSeconds !== undefined && (
              <>
                <div
                  className="timeline-preview-line"
                  style={{ left: `${(timelinePreviewSeconds / daySeconds) * 100}%` }}
                  aria-hidden="true"
                />
                <output
                  className="timeline-time-tooltip"
                  style={{ left: `${(timelinePreviewSeconds / daySeconds) * 100}%` }}
                >
                  {formatDayTime(timelinePreviewSeconds)}
                </output>
              </>
            )}
            <input
              className="timeline-scrubber"
              aria-label={t('playback.timelineLabel')}
              type="range"
              min="0"
              max={daySeconds - 1}
              step="1"
              value={Math.floor(playheadSeconds)}
              disabled={timelineSegments.length === 0 || querying || openingPlayback || seeking}
              aria-valuetext={formatDayTime(playheadSeconds)}
              onChange={(event) => {
                const second = Number(event.target.value);
                updatePlayhead(second);
                setTimelinePreviewSeconds(second);
              }}
              onPointerDown={beginTimelineDrag}
              onPointerMove={previewTimeline}
              onPointerUp={finishTimelineDrag}
              onPointerCancel={() => setTimelineDragging(false)}
              onFocus={() => setTimelinePreviewSeconds(playheadRef.current)}
              onBlur={() => { if (!timelineDragging) setTimelinePreviewSeconds(undefined); }}
              onKeyUp={(event) => {
                const second = Number(event.currentTarget.value);
                setTimelinePreviewSeconds(second);
                void seekToDaySecond(second);
              }}
            />
            <div className="timeline-labels"><span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span></div>
          </div>
        </div>

        <aside className="segment-list">
          <h3>{t('playback.segments')}</h3>
          {segments.map((segment) => {
            const key = segmentKey(segment);
            return (
              <button
                className={activeSegmentKey === key ? 'active' : ''}
                key={key}
                onClick={() => void playAt(segment)}
                disabled={openingPlayback || querying || seeking}
              >
                <strong>{formatNvrDateTime(segment.startLocalDateTime)}</strong>
                <span>{formatNvrDateTime(segment.endLocalDateTime)}</span>
              </button>
            );
          })}
          {segments.length === 0 && <p className="muted">{t('playback.noSegments')}</p>}
        </aside>
      </div>
    </section>
  );
}
